import { useRef, useEffect, useState, useCallback } from "react";
import { Button } from "./ui/button";
import { PencilSimple, Circle, ArrowRight, Square, ArrowCounterClockwise, ArrowClockwise, FloppyDisk, X } from "@phosphor-icons/react";

const COLORS = ["#ef4444", "#f97316", "#eab308", "#22c55e", "#3b82f6", "#8b5cf6", "#ffffff", "#000000"];
const TOOLS = [
  { id: "freehand", icon: PencilSimple, label: "Draw" },
  { id: "circle", icon: Circle, label: "Circle" },
  { id: "arrow", icon: ArrowRight, label: "Arrow" },
  { id: "rect", icon: Square, label: "Rectangle" },
];
const WIDTHS = [2, 4, 6];

export function ImageAnnotator({ imageSrc, onSave, onCancel }) {
  const canvasRef = useRef(null);
  const overlayRef = useRef(null);
  const [tool, setTool] = useState("freehand");
  const [color, setColor] = useState("#ef4444");
  const [strokeWidth, setStrokeWidth] = useState(4);
  const [drawing, setDrawing] = useState(false);
  const [startPos, setStartPos] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyIdx, setHistoryIdx] = useState(-1);
  const [imgLoaded, setImgLoaded] = useState(false);
  const imgRef = useRef(null);
  const sizeRef = useRef({ w: 0, h: 0 });

  // Load image and init canvas
  useEffect(() => {
    const img = new window.Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      imgRef.current = img;
      const maxW = Math.min(img.width, 900);
      const scale = maxW / img.width;
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      sizeRef.current = { w, h };

      const canvas = canvasRef.current;
      const overlay = overlayRef.current;
      if (!canvas || !overlay) return;
      canvas.width = w;
      canvas.height = h;
      overlay.width = w;
      overlay.height = h;

      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, w, h);
      // Save initial state
      const snap = ctx.getImageData(0, 0, w, h);
      setHistory([snap]);
      setHistoryIdx(0);
      setImgLoaded(true);
    };
    img.src = imageSrc;
  }, [imageSrc]);

  const saveSnapshot = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const snap = ctx.getImageData(0, 0, canvas.width, canvas.height);
    setHistory(prev => {
      const trimmed = prev.slice(0, historyIdx + 1);
      return [...trimmed, snap];
    });
    setHistoryIdx(prev => prev + 1);
  }, [historyIdx]);

  const undo = useCallback(() => {
    if (historyIdx <= 0) return;
    const newIdx = historyIdx - 1;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.putImageData(history[newIdx], 0, 0);
    setHistoryIdx(newIdx);
  }, [historyIdx, history]);

  const redo = useCallback(() => {
    if (historyIdx >= history.length - 1) return;
    const newIdx = historyIdx + 1;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.putImageData(history[newIdx], 0, 0);
    setHistoryIdx(newIdx);
  }, [historyIdx, history]);

  const getPos = (e) => {
    const rect = overlayRef.current.getBoundingClientRect();
    const touch = e.touches ? e.touches[0] : e;
    return { x: touch.clientX - rect.left, y: touch.clientY - rect.top };
  };

  const startDraw = (e) => {
    e.preventDefault();
    const pos = getPos(e);
    setDrawing(true);
    setStartPos(pos);
    if (tool === "freehand") {
      const ctx = canvasRef.current.getContext("2d");
      ctx.beginPath();
      ctx.moveTo(pos.x, pos.y);
      ctx.strokeStyle = color;
      ctx.lineWidth = strokeWidth;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
    }
  };

  const onDraw = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const pos = getPos(e);
    if (tool === "freehand") {
      const ctx = canvasRef.current.getContext("2d");
      ctx.lineTo(pos.x, pos.y);
      ctx.stroke();
    } else {
      // Draw preview on overlay canvas
      const overlay = overlayRef.current;
      const octx = overlay.getContext("2d");
      octx.clearRect(0, 0, overlay.width, overlay.height);
      octx.strokeStyle = color;
      octx.lineWidth = strokeWidth;
      octx.lineCap = "round";
      octx.lineJoin = "round";
      drawShape(octx, startPos, pos);
    }
  };

  const endDraw = (e) => {
    if (!drawing) return;
    setDrawing(false);
    const pos = e.changedTouches ? { x: e.changedTouches[0].clientX - overlayRef.current.getBoundingClientRect().left, y: e.changedTouches[0].clientY - overlayRef.current.getBoundingClientRect().top } : getPos(e);

    if (tool !== "freehand" && startPos) {
      // Commit shape to main canvas
      const ctx = canvasRef.current.getContext("2d");
      ctx.strokeStyle = color;
      ctx.lineWidth = strokeWidth;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      drawShape(ctx, startPos, pos);
      // Clear overlay
      const overlay = overlayRef.current;
      overlay.getContext("2d").clearRect(0, 0, overlay.width, overlay.height);
    }
    saveSnapshot();
    setStartPos(null);
  };

  const drawShape = (ctx, from, to) => {
    ctx.beginPath();
    if (tool === "circle") {
      const rx = Math.abs(to.x - from.x) / 2;
      const ry = Math.abs(to.y - from.y) / 2;
      const cx = (from.x + to.x) / 2;
      const cy = (from.y + to.y) / 2;
      ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
      ctx.stroke();
    } else if (tool === "arrow") {
      const headLen = 14 + strokeWidth * 2;
      const angle = Math.atan2(to.y - from.y, to.x - from.x);
      // Shaft
      ctx.moveTo(from.x, from.y);
      ctx.lineTo(to.x, to.y);
      ctx.stroke();
      // Arrowhead
      ctx.beginPath();
      ctx.moveTo(to.x, to.y);
      ctx.lineTo(to.x - headLen * Math.cos(angle - Math.PI / 6), to.y - headLen * Math.sin(angle - Math.PI / 6));
      ctx.moveTo(to.x, to.y);
      ctx.lineTo(to.x - headLen * Math.cos(angle + Math.PI / 6), to.y - headLen * Math.sin(angle + Math.PI / 6));
      ctx.stroke();
    } else if (tool === "rect") {
      ctx.strokeRect(from.x, from.y, to.x - from.x, to.y - from.y);
    }
  };

  const handleSave = () => {
    const canvas = canvasRef.current;
    canvas.toBlob((blob) => {
      if (blob) onSave(blob);
    }, "image/png");
  };

  return (
    <div className="flex flex-col gap-3" data-testid="image-annotator">
      {/* Toolbar */}
      <div className="flex items-center gap-2 flex-wrap bg-muted/50 rounded-lg p-2" data-testid="annotator-toolbar">
        {/* Tools */}
        <div className="flex items-center gap-1 border-r border-border pr-2">
          {TOOLS.map(t => (
            <button
              key={t.id}
              onClick={() => setTool(t.id)}
              className={`p-1.5 rounded-md transition-colors ${tool === t.id ? "bg-primary text-primary-foreground" : "hover:bg-muted text-muted-foreground"}`}
              title={t.label}
              data-testid={`tool-${t.id}`}
            >
              <t.icon size={18} weight={tool === t.id ? "fill" : "regular"} />
            </button>
          ))}
        </div>

        {/* Colors */}
        <div className="flex items-center gap-1 border-r border-border pr-2" data-testid="color-picker">
          {COLORS.map(c => (
            <button
              key={c}
              onClick={() => setColor(c)}
              className={`w-5 h-5 rounded-full border-2 transition-transform ${color === c ? "border-foreground scale-125" : "border-transparent"}`}
              style={{ backgroundColor: c }}
              data-testid={`color-${c.replace("#", "")}`}
            />
          ))}
        </div>

        {/* Stroke Width */}
        <div className="flex items-center gap-1 border-r border-border pr-2" data-testid="stroke-width">
          {WIDTHS.map(w => (
            <button
              key={w}
              onClick={() => setStrokeWidth(w)}
              className={`flex items-center justify-center w-7 h-7 rounded-md transition-colors ${strokeWidth === w ? "bg-primary text-primary-foreground" : "hover:bg-muted text-muted-foreground"}`}
              data-testid={`width-${w}`}
            >
              <div className="rounded-full bg-current" style={{ width: w + 2, height: w + 2 }} />
            </button>
          ))}
        </div>

        {/* Undo/Redo */}
        <div className="flex items-center gap-1">
          <button onClick={undo} disabled={historyIdx <= 0} className="p-1.5 rounded-md hover:bg-muted text-muted-foreground disabled:opacity-30" title="Undo" data-testid="undo-btn">
            <ArrowCounterClockwise size={18} />
          </button>
          <button onClick={redo} disabled={historyIdx >= history.length - 1} className="p-1.5 rounded-md hover:bg-muted text-muted-foreground disabled:opacity-30" title="Redo" data-testid="redo-btn">
            <ArrowClockwise size={18} />
          </button>
        </div>

        {/* Save / Cancel */}
        <div className="flex items-center gap-1 ml-auto">
          <Button variant="ghost" size="sm" onClick={onCancel} data-testid="annotator-cancel-btn">
            <X size={14} className="mr-1" /> Cancel
          </Button>
          <Button size="sm" onClick={handleSave} disabled={!imgLoaded} data-testid="annotator-save-btn">
            <FloppyDisk size={14} className="mr-1" /> Save
          </Button>
        </div>
      </div>

      {/* Canvas Area */}
      <div className="relative border border-border rounded-lg overflow-auto bg-[#1a1a1a]" style={{ minHeight: 200, maxHeight: 500 }}>
        {!imgLoaded && (
          <div className="absolute inset-0 flex items-center justify-center z-10">
            <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
          </div>
        )}
        <div className="relative inline-block">
          <canvas ref={canvasRef} className="block" data-testid="annotator-canvas" />
          <canvas
            ref={overlayRef}
            className="absolute top-0 left-0"
            style={{ cursor: "crosshair" }}
            onMouseDown={startDraw}
            onMouseMove={onDraw}
            onMouseUp={endDraw}
            onMouseLeave={(e) => { if (drawing) endDraw(e); }}
            onTouchStart={startDraw}
            onTouchMove={onDraw}
            onTouchEnd={endDraw}
            data-testid="annotator-overlay"
          />
        </div>
      </div>

      <p className="text-[10px] text-muted-foreground text-center">
        Select a tool and draw on the image. Use undo/redo to fix mistakes. Click Save when done.
      </p>
    </div>
  );
}
